export const MINIMUM_PASSWORD_LENGTH = 12;
export const MAXIMUM_PASSWORD_LENGTH = 128;
export const RESET_REQUEST_COOLDOWN_SECONDS = 60;
export const INVITATION_TENANT_QUERY_PARAMETER = 'invitation_tenant_id';
export const INVITATION_PROOF_QUERY_PARAMETER = 'invitation_proof';

const COMMON_PASSWORDS = new Set([
  'password123!',
  'password1234',
  'qwerty123456',
  'letmein123456',
  'welcome12345',
  'changeme123!',
]);

const text = value => String(value ?? '').trim();

export function normalizeAuthEmail(value) {
  return text(value).toLowerCase();
}

export function authEmailValidation(value) {
  const email = normalizeAuthEmail(value);
  if (!email) return 'Enter your email address.';
  if (email.length > 254
      || /\s/.test(email)
      || !/^[^@]+@[^@]+\.[^@]+$/.test(email)
      || email.includes('..')) {
    return 'Enter a valid email address.';
  }
  return '';
}

function passwordCharacterClassCount(password) {
  return [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9\s]/]
    .reduce((count, pattern) => count + Number(pattern.test(password)), 0);
}

export function passwordPolicyChecks(value, email = '') {
  const password = String(value ?? '');
  const normalizedEmail = normalizeAuthEmail(email);
  const localPart = normalizedEmail.split('@')[0] || '';
  const passphraseWords = password.trim().split(/\s+/).filter(Boolean);
  const hasPassphraseStrength = password.length >= 16 && passphraseWords.length >= 3;
  const hasCharacterStrength = passwordCharacterClassCount(password) >= 3;
  const normalizedPassword = password.toLowerCase().replace(/\s+/g, '');

  return Object.freeze({
    minimumLength:password.length >= MINIMUM_PASSWORD_LENGTH,
    maximumLength:password.length <= MAXIMUM_PASSWORD_LENGTH,
    supportedCharacters:!/[\u0000-\u001f\u007f]/.test(password),
    characterStrength:hasCharacterStrength || hasPassphraseStrength,
    excludesEmail:localPart.length < 4 || !password.toLowerCase().includes(localPart),
    notCommon:!COMMON_PASSWORDS.has(normalizedPassword),
  });
}

export function passwordPolicyError(password, confirmation = undefined, email = '') {
  const checks = passwordPolicyChecks(password, email);
  if (!checks.minimumLength) return `Use at least ${MINIMUM_PASSWORD_LENGTH} characters.`;
  if (!checks.maximumLength) return `Use no more than ${MAXIMUM_PASSWORD_LENGTH} characters.`;
  if (!checks.supportedCharacters) return 'Do not use control characters in the password.';
  if (!checks.characterStrength) {
    return 'Use at least three of: uppercase letters, lowercase letters, numbers, and symbols; or use a 16-character passphrase with at least three words.';
  }
  if (!checks.excludesEmail) return 'Do not include your email address in the password.';
  if (!checks.notCommon) return 'Choose a less common password.';
  if (confirmation !== undefined && password !== confirmation) return 'The passwords do not match.';
  return '';
}

function locationParameters(location) {
  const search = new URLSearchParams(String(location?.search || ''));
  const hash = new URLSearchParams(String(location?.hash || '').replace(/^#/, ''));
  const first = key => search.get(key) || hash.get(key) || '';
  return { search, hash, first };
}

export function authCallbackDetails(location = globalThis?.window?.location) {
  if (!location) {
    return Object.freeze({
      flow:'', code:'', accessToken:'', refreshToken:'', errorCode:'',
      invitationTenantId:'', invitationProof:'', hasCallback:false,
    });
  }
  const { search, hash, first } = locationParameters(location);
  const candidate = text(
    first('auth_flow')
    || (first('reset') === 'true' ? 'recovery' : '')
    || first('type'),
  ).toLowerCase();
  const flow = candidate === 'recovery'
    ? 'recovery'
    : candidate === 'invite' || candidate === 'signup'
      ? 'invite'
      : '';
  const code = text(search.get('code'));
  const accessToken = text(hash.get('access_token'));
  const refreshToken = text(hash.get('refresh_token'));
  const errorCode = text(first('error_code') || first('error')).toLowerCase();
  const invitationTenantId = text(first(INVITATION_TENANT_QUERY_PARAMETER));
  const invitationProof = text(first(INVITATION_PROOF_QUERY_PARAMETER));
  return Object.freeze({
    flow,
    code,
    accessToken,
    refreshToken,
    errorCode,
    invitationTenantId,
    invitationProof,
    hasCallback:Boolean(flow || code || accessToken || refreshToken || errorCode),
  });
}

export function hasAuthenticationCallback(location = globalThis?.window?.location) {
  return authCallbackDetails(location).hasCallback;
}

export function authCallbackUserMessage(details, flow = details?.flow || '') {
  const code = text(details?.errorCode).toLowerCase();
  if (code === 'otp_expired' || code === 'expired' || code === 'access_denied') {
    return flow === 'invite'
      ? 'This invitation link has expired or has already been used. Ask an administrator to resend the invitation.'
      : 'This password-reset link has expired or has already been used. Return to sign in and request a new link.';
  }
  return flow === 'invite'
    ? 'This invitation link is invalid or no longer available. Ask an administrator to resend the invitation.'
    : 'This password-reset link is invalid or no longer available. Return to sign in and request a new link.';
}

export function isLikelyNetworkError(error) {
  const message = String(error?.message || '').toLowerCase();
  return /failed to fetch|network|load failed|offline|connection/.test(message);
}

export function isAuthEmailRateLimit(error) {
  const status = Number(error?.status || error?.context?.status || 0);
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();
  return status === 429 || code.includes('rate') || /rate.?limit|too many requests|email.*limit/.test(message);
}
